import { api } from './api'

export type BannerNotificationType = 'banner' | 'session-discussion' | 'community-post' | string

export interface BannerData {
  id: string
  active: boolean
  message: string
  type?: 'info' | 'warning' | 'error' | 'success'
  dismissible?: boolean
  dismissed: boolean
  link?: string | null
  linkText?: string | null
  notificationType?: BannerNotificationType
  sessionId?: number | null
  postId?: string | null
  postTitle?: string | null
  createdAt?: string | null
  expiresAt?: string | null
}

export interface BannerResponse {
  success: boolean
  banner: BannerData | null
  banners?: BannerData[]
  notifications?: BannerData[]
  communityNotifications?: BannerData[]
}

export interface DismissBannerResponse {
  success: boolean
  notificationId: string
  notificationType?: BannerNotificationType
  dismissed: boolean
}

function isVisibleBanner(item: BannerData | null | undefined): item is BannerData {
  return Boolean(item && item.active !== false && item.dismissed !== true)
}

function getCreatedTime(item: BannerData): number {
  if (!item.createdAt) return 0
  const timestamp = new Date(item.createdAt).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

function uniqueVisibleItems(items: Array<BannerData | null | undefined>): BannerData[] {
  const itemMap = new Map<string, BannerData>()

  items.forEach(item => {
    if (isVisibleBanner(item) && !itemMap.has(item.id)) {
      itemMap.set(item.id, item)
    }
  })

  return Array.from(itemMap.values()).sort((a, b) => getCreatedTime(b) - getCreatedTime(a))
}

function normalizeBannerResponse(data: BannerResponse): BannerResponse {
  const notifications = uniqueVisibleItems([
    ...(data.notifications ?? []),
    ...(data.communityNotifications ?? []),
  ])
  const communityNotificationItems = data.communityNotifications?.length
    ? data.communityNotifications
    : notifications.filter(item => item.notificationType === 'community-post')
  const communityNotifications = uniqueVisibleItems(
    communityNotificationItems,
  )
  const banners = uniqueVisibleItems([
    data.banner,
    ...(data.banners ?? []),
    ...notifications,
    ...communityNotifications,
  ])
  const banner = isVisibleBanner(data.banner) ? data.banner : banners[0] ?? null

  return {
    ...data,
    banner,
    banners,
    notifications,
    communityNotifications,
  }
}

export const bannerService = {
  // Get current active banner
  getActiveBanner: async (): Promise<BannerResponse> => {
    try {
      // Tenant is injected into /security/* by the axios interceptor
      const data = await api.get<BannerResponse>('/security/banner/active')
      return normalizeBannerResponse(data)
    } catch (error) {
      return { success: false, banner: null, banners: [], notifications: [], communityNotifications: [] }
    }
  },

  // Persist a dismissible banner dismissal for the logged-in user.
  dismissBanner: async (bannerId: string): Promise<DismissBannerResponse> => {
    return api.post<DismissBannerResponse>(`/security/banner/${encodeURIComponent(bannerId)}/dismiss`)
  }
}
